import { useCallback, useMemo, useState } from "react";
import { bingoSquares } from "../progress/bingoData";
import { cn } from "@/lib/utils";
import { BingoSquare, TypeFormResponse } from "@/types/bingo";
import { normalizedTypeFormResponse } from "@/lib/typeform";
import FileViewer from "./FileViewer";

type Props = {
  email: string;
  userId: string;
  isDone: boolean;
  isHidden: boolean;
  formId: string;
  onUpdateStatus: (
    userId: string,
    formId: string,
    isDone: boolean,
    isHidden: boolean
  ) => Promise<unknown>;
  response: TypeFormResponse;
};

const BingoResponseListItem = (props: Props) => {
  const { email, userId, isDone, formId, onUpdateStatus, response, isHidden } = props;
  const [isLoading, setIsLoading] = useState(false);

  const toggleStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      await onUpdateStatus(userId, formId, !isDone, isHidden);
    } finally {
      setIsLoading(false);
    }
  }, [formId, isDone, onUpdateStatus, userId, isHidden]);

  const toggleHiding = useCallback(async () => {
    setIsLoading(true);
    try {
      await onUpdateStatus(userId, formId, isDone, !isHidden);
    } finally {
      setIsLoading(false);
    }
  }, [formId, isDone, onUpdateStatus, userId, isHidden]);

  const bingoData: BingoSquare | undefined = useMemo(() => {
    return bingoSquares.find((square) => {
      return square.type ==="form" && square.formId === formId;
    });
  }, [formId]);

  const bingoTitle = bingoData?.text || "Unknown bingo card";

  const normalizedAnswers = useMemo(() => {
    return normalizedTypeFormResponse(response);
  }, [response]);

  return (
    <div className="bg-card rounded-lg shadow p-4 w-full space-y-2">
      <h3 className="text-lg font-medium">
        <span
          data-done={isDone}
          className="py-1 px-2 rounded-full text-xs data-[done=true]:bg-success data-[done=true]:text-success-foreground data-[done=false]:bg-muted data-[done=false]:text-muted-foreground"
        >
          {isDone ? "Done" : "Waiting"}
        </span>
        &nbsp;
        <span>{bingoTitle}</span>
      </h3>
      {/* user info */}
      <div className="w-full flex flex-col text-xs text-muted-foreground">
        <code className="w-fit">UserId: {userId ?? "Anonymous"}</code>
        <code className="w-fit">Email: {email}</code>
      </div>

      {/* response */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-4">
        {normalizedAnswers.map(({ question, answer, fileUrl }, index) => {
          return (
            <div key={index} className="space-y-1 border-b border-border">
              <div className="text-sm text-muted-foreground">{question}</div>
              <p className="text-sm font-medium empty:hidden h-fit max-h-[100px] w-full overflow-y-scroll">
                {answer}
              </p>
              <FileViewer fileUrl={fileUrl} />
            </div>
          );
        })}
      </div>

      {/* action button */}
      <div className="w-full inline-flex items-center justify-start gap-4 pt-4">
        {/* update status */}
        <button
          onClick={toggleStatus}
          disabled={isLoading}
          className={cn(
            "p-2 text-sm rounded-lg disabled:opacity-60 transition-all hover:shadow",
            isDone && "bg-muted text-foreground hover:bg-muted/80",
            !isDone && "bg-success text-success-foreground hover:bg-success/90"
          )}
        >
          {isDone ? "Unmark as Done" : "Mark as Completed"}
        </button>
        {/* toggle hiding */}
        <button
          onClick={toggleHiding}
          disabled={isLoading}
          className={cn(
            "p-2 text-sm rounded-lg disabled:opacity-60 transition-all hover:shadow",
            !isHidden && "bg-muted text-foreground hover:bg-muted/80",
            isHidden && "bg-success text-success-foreground hover:bg-success/90"
          )}
        >
          {isHidden ? "Unhide response" : "Hide response"}
        </button>
      </div>
    </div>
  );
};

export default BingoResponseListItem;